<script setup>
import { reactive, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import AuthLayout from "@/layouts/AuthLayout.vue";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { Lock, Mail, ArrowRight } from "lucide-vue-next";
import api from "@/lib/axios";

const router = useRouter();
const route = useRoute();
const { login, isLoading } = useAuth();
const { toast } = useToast();

const form = reactive({
  email: "",
  password: "",
});

// Check for verification status from URL params
onMounted(() => {
  const verified = route.query.verified;
  const error = route.query.error;
  const message = route.query.message;

  if (verified === '1' && message) {
    toast({
      title: "Email verificado",
      description: decodeURIComponent(message),
    });
    // Clean URL params
    router.replace({ path: '/login', query: {} });
  } else if (error && message) {
    toast({
      title: "Error de verificación",
      description: decodeURIComponent(message),
      variant: "destructive",
    });
    router.replace({ path: '/login', query: {} });
  }
});

const handleLogin = async (e) => {
  e.preventDefault();

  try {
    await login(form);
    toast({
      title: "Bienvenido de nuevo",
      description: "Has iniciado sesión correctamente en Fenix Guardian.",
    });
  } catch (error) {
    // Check for email not verified error
    const errorCode = error.response?.data?.error?.code;
    const errorMessage = error.response?.data?.error?.message;
    
    if (errorCode === 'EMAIL_NOT_VERIFIED') {
      toast({
        title: "Email no verificado",
        description: errorMessage || "Por favor verifica tu correo electrónico antes de iniciar sesión.",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Error de acceso",
        description: "Credenciales incorrectas o error en el servidor.",
        variant: "destructive",
      });
    }
  }
};

const handleGoogleLogin = async () => {
  try {
    const { data } = await api.get('/api/v1/auth/google/redirect');
    if (data.url) {
        window.location.href = data.url;
    }
  } catch (error) {
    console.error(error);
    toast({
      title: "Error",
      description: "No se pudo iniciar la autenticación con Google.",
      variant: "destructive",
    });
  }
};
</script>

<template>
  <AuthLayout>
    <div class="text-center mb-8">
      <h1 class="text-2xl font-bold text-foreground mb-2">Iniciar Sesión</h1>
      <p class="text-sm text-muted-foreground">
        Accede al panel de control de Fenix Guardian
      </p>
    </div>

    <form @submit="handleLogin" class="space-y-5">
      <div class="space-y-2">
        <Label for="email">Correo electrónico</Label>
        <div class="relative">
          <Mail class="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input 
            id="email" 
            type="email" 
            v-model="form.email"
            placeholder="admin@empresa.com" 
            class="pl-9 bg-background/50 border-border/50 focus:border-accent" 
            required
          />
        </div>
      </div>

      <div class="space-y-2">
        <div class="flex items-center justify-between">
          <Label for="password">Contraseña</Label>
          <router-link 
            to="/forgot-password" 
            class="text-xs text-accent hover:text-accent/80 transition-colors"
          >
            ¿Olvidaste tu contraseña?
          </router-link>
        </div>
        <div class="relative">
          <Lock class="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input 
            id="password" 
            type="password" 
            v-model="form.password"
            placeholder="••••••••" 
            class="pl-9 bg-background/50 border-border/50 focus:border-accent" 
            required
          />
        </div>
      </div>

      <Button 
        type="submit" 
        variant="hero-primary" 
        class="w-full group" 
        :disabled="isLoading"
      >
        <span v-if="isLoading">Iniciando sesión...</span>
        <span v-else class="flex items-center gap-2">
          Entrar al sistema
          <ArrowRight class="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </span>
      </Button>
    </form>

    <div class="relative my-6">
      <div class="absolute inset-0 flex items-center">
        <span class="w-full border-t border-border/50" />
      </div>
      <div class="relative flex justify-center text-xs uppercase">
        <span class="bg-background px-2 text-muted-foreground">
          O continuar con
        </span>
      </div>
    </div>

    <Button 
      type="button" 
      variant="outline" 
      class="w-full bg-background hover:bg-accent/10 border-border/50"
      @click="handleGoogleLogin"
      :disabled="isLoading"
    >
      <svg class="h-4 w-4 mr-2" aria-hidden="true" viewBox="0 0 24 24">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
      Google
    </Button>

    <template #footer>
      <p class="text-sm text-muted-foreground">
        ¿No tienes una cuenta?
        <router-link 
          to="/register" 
          class="font-medium text-foreground hover:text-accent transition-colors underline underline-offset-4"
        >
          Solicitar acceso
        </router-link>
      </p>
    </template>
  </AuthLayout>
</template>
